import { inject, Injectable } from "@angular/core";
import { LoggedUserService } from "@keus-template/auth";
import { UserWeeklyScheduleExceptionsService } from "../../../api/services";
import { Observable } from "rxjs";
import { UserWeeklyScheduleExceptionsControllerCreate$Params } from "../../../api/fn/user-weekly-schedule-exceptions/user-weekly-schedule-exceptions-controller-create";
import { environment } from "../../../environments/environment";
import {
  CreateUserWeeklyScheduleExceptionDto,
  ResponseUserWeeklyScheduleExceptionDto,
  TimeRangeDto,
  UpdateUserWeeklyScheduleExceptionDto,
} from "../../../api/models";
import { UserWeeklyScheduleExceptionsControllerUpdate$Params } from "../../../api/fn/user-weekly-schedule-exceptions/user-weekly-schedule-exceptions-controller-update";
import { UserWeeklyScheduleExceptionsControllerDelete$Params } from "../../../api/fn/user-weekly-schedule-exceptions/user-weekly-schedule-exceptions-controller-delete";
import { UserWeeklyScheduleExceptionsControllerFindOne$Params } from "../../../api/fn/user-weekly-schedule-exceptions/user-weekly-schedule-exceptions-controller-find-one";
import { UserWeeklyScheduleExceptionsControllerFindAllByEmail$Params } from "../../../api/fn/user-weekly-schedule-exceptions/user-weekly-schedule-exceptions-controller-find-all-by-email";

@Injectable({
  providedIn: "root",
})
export class UserWeeklyScheduleExceptionsApiService {
  /** Injected service for logged user */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** Service for interacting with the User Weekly Schedule API. */
  private readonly _userWeeklyScheduleExceptionsService: UserWeeklyScheduleExceptionsService =
    inject(UserWeeklyScheduleExceptionsService);

  /**
   * Creates a weekly schedule exception for the logged-in user on a specific date.
   *
   * @param date The date for the exception (ISO string).
   * @param availability Optional array of available time ranges on that date.
   * @param unvailable Optional flag to mark the user as completely unavailable on that date.
   * @returns Observable of the created schedule exception.
   */
  public create$(
    date: string,
    availability?: TimeRangeDto[],
    unvailable?: boolean,
  ): Observable<ResponseUserWeeklyScheduleExceptionDto> {
    const email: string = this._loggedUserService.currentUser().email;

    const body: CreateUserWeeklyScheduleExceptionDto = {
      tenant: environment.tenant,
      email,
      date,
      availability,
      unvailable,
    };

    const params: UserWeeklyScheduleExceptionsControllerCreate$Params = {
      body,
    };

    return this._userWeeklyScheduleExceptionsService.userWeeklyScheduleExceptionsControllerCreate(
      params,
    );
  }

  /**
   * Updates an existing weekly schedule exception for the logged-in user.
   *
   * @param createdAt The creation timestamp of the exception to update.
   * @param body The updated data for the exception.
   * @returns Observable of the updated schedule exception.
   */
  public update$(
    createdAt: string,
    body: UpdateUserWeeklyScheduleExceptionDto,
  ): Observable<ResponseUserWeeklyScheduleExceptionDto> {
    const email: string = this._loggedUserService.currentUser().email;

    const params: UserWeeklyScheduleExceptionsControllerUpdate$Params = {
      tenant: environment.tenant,
      email,
      createdAt,
      body,
    };

    return this._userWeeklyScheduleExceptionsService.userWeeklyScheduleExceptionsControllerUpdate(
      params,
    );
  }

  /**
   * Deletes a weekly schedule exception for the logged-in user.
   *
   * @param createdAt The creation timestamp of the exception to delete.
   * @returns Observable of void when the deletion is successful.
   */
  public delete$(createdAt: string): Observable<void> {
    const email: string = this._loggedUserService.currentUser().email;

    const params: UserWeeklyScheduleExceptionsControllerDelete$Params = {
      tenant: environment.tenant,
      email,
      createdAt,
    };

    return this._userWeeklyScheduleExceptionsService.userWeeklyScheduleExceptionsControllerDelete(
      params,
    );
  }

  /**
   * Retrieves a specific weekly schedule exception by its creation timestamp.
   *
   * @param createdAt The creation timestamp of the exception to retrieve.
   * @returns Observable of the found schedule exception.
   */
  public findOne$(
    createdAt: string,
  ): Observable<ResponseUserWeeklyScheduleExceptionDto> {
    const email: string = this._loggedUserService.currentUser().email;

    const params: UserWeeklyScheduleExceptionsControllerFindOne$Params = {
      tenant: environment.tenant,
      email,
      createdAt,
    };

    return this._userWeeklyScheduleExceptionsService.userWeeklyScheduleExceptionsControllerFindOne(
      params,
    );
  }

  /**
   * Retrieves all weekly schedule exceptions for the logged-in user.
   *
   * @returns Observable of an array of schedule exceptions for the user.
   */
  public findAllByEmail$(): Observable<
    ResponseUserWeeklyScheduleExceptionDto[]
  > {
    const email: string = this._loggedUserService.currentUser().email;

    const params: UserWeeklyScheduleExceptionsControllerFindAllByEmail$Params =
      {
        tenant: environment.tenant,
        email,
      };

    return this._userWeeklyScheduleExceptionsService.userWeeklyScheduleExceptionsControllerFindAllByEmail(
      params,
    );
  }
}
